// İzin (leave) ortak yardımcıları — etiketler, gün hesabı ve bakiye.
// ProfilePage (talep formu), LeaveRequestsPage (yönetim + rapor) ve
// EmployeeLeaveModal tarafından paylaşılan tek doğruluk kaynağıdır.

import { getHolidayMapForRange, toDateKey } from './turkishHolidays';

// İzin türü kodlarının Türkçe karşılıkları (statik).
export const LEAVE_TYPE_LABELS = {
  yillik: 'Yıllık İzin',
  mazeret: 'Mazeret İzni',
  raporlu: 'Raporlu',
  dogum_oncesi: 'Doğum Öncesi İzin',
  babalik: 'Babalık İzni',
  evlilik: 'Evlilik İzni',
  olum: 'Ölüm İzni',
  engelli_cocuk: 'Engelli Çocuk Mazeret İzni',
  dogum_sonrasi: 'Doğum Sonrası İzin',
  yatakli_tedavi: 'Yataklı Kurumda Tedavi',
  refakat: 'Refakat İzni',
  sua: 'Şua İzni',
  saglik_kurulu: 'Sağlık Kurulu Raporu',
  ayakta_tedavi: 'Ayakta Tedavi',
};

export const translateLeaveType = (type) => LEAVE_TYPE_LABELS[type] || type;

// Bakiyeden düşen / kotaya tabi izin türü.
export const ANNUAL_LEAVE_KEY = 'yillik';

// Yeni çalışan için varsayılan yıllık izin (gün).
export const DEFAULT_ANNUAL_PERMIT = 14;

// Başlangıç ve bitiş tarihine göre izin gün sayısını hesaplar.
// Cumartesi = 0.5 gün, Pazar = 0 gün, hafta içi = 1 gün.
// Resmi tatiller (Türkiye) izin süresinden DÜŞÜLMEZ:
//   tam gün tatil → 0 gün, yarım gün (arefe) → en fazla 0,5 gün sayılır.
export const calculateLeaveDays = (startDate, endDate) => {
  if (!startDate || !endDate) return 0;
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;
  if (end < start) return 0;

  const holidayMap = getHolidayMapForRange(start, end);

  let totalDays = 0;
  const current = new Date(start);
  while (current <= end) {
    const dayOfWeek = current.getDay();
    let dayValue;
    if (dayOfWeek === 0) {
      dayValue = 0; // Pazar
    } else if (dayOfWeek === 6) {
      dayValue = 0.5; // Cumartesi
    } else {
      dayValue = 1; // Hafta içi
    }

    const holiday = holidayMap.get(toDateKey(current));
    if (holiday) {
      // Tam gün tatil hiç sayılmaz; arefe (yarım gün) en fazla 0,5 sayılır.
      dayValue = holiday.type === 'full' ? 0 : Math.min(dayValue, 0.5);
    }

    totalDays += dayValue;
    current.setDate(current.getDate() + 1);
  }
  return totalDays;
};

// Çalışanın yıllık izin hakkı (gün). Tanımsızsa varsayılan 14.
export const getAnnualEntitlement = (employee) =>
  Number(employee?.annualPermit) || DEFAULT_ANNUAL_PERMIT;

// Tarih alanından yıl çıkarır (string veya Firestore Timestamp).
const yearOf = (dateValue) => {
  if (!dateValue) return null;
  try {
    const d = dateValue.toDate ? dateValue.toDate() : new Date(dateValue);
    return Number.isNaN(d.getTime()) ? null : d.getFullYear();
  } catch {
    return null;
  }
};

// Bir kaydın yıllık izin olup olmadığını belirler.
// LeaveRequests'te alan `leaveType`, EmployeeLeaves'te `typeKey` olabilir.
const isAnnualLeave = (record) =>
  (record.leaveType || record.typeKey) === ANNUAL_LEAVE_KEY;

/**
 * Belirli bir yıl için yıllık izin bakiyesini hesaplar.
 * @returns {{ entitlement:number, used:number, reserved:number, remaining:number }}
 *
 * used     = ONAYLANMIŞ yıllık izin günleri (LeaveRequests + EmployeeLeaves)
 * reserved = BEKLEYEN (PENDING) yıllık izin talepleri (LeaveRequests)
 * remaining = max(0, entitlement - used - reserved)
 *
 * @param entitlement      Çalışanın yıllık izin hakkı (gün)
 * @param leaveRequests    Çalışana ait LeaveRequests kayıtları
 * @param employeeLeaves   Çalışana ait EmployeeLeaves kayıtları (admin manuel)
 * @param year             Hesaplanacak takvim yılı (varsayılan: bu yıl)
 * @param excludeRequestId Hesaptan çıkarılacak talep id'si (taslak/düzenleme)
 */
export const computeAnnualBalance = ({
  entitlement,
  leaveRequests = [],
  employeeLeaves = [],
  year = new Date().getFullYear(),
  excludeRequestId = null,
}) => {
  const total = Number(entitlement) || 0;

  const sumDays = (records, statuses) =>
    records
      .filter((r) => {
        if (excludeRequestId && r.id === excludeRequestId) return false;
        if (!isAnnualLeave(r)) return false;
        if (yearOf(r.startDate) !== year) return false;
        // EmployeeLeaves eski kayıtlarda status olmayabilir → onaylanmış say.
        const status = r.status || 'APPROVED';
        return statuses.includes(status);
      })
      .reduce((acc, r) => acc + (Number(r.leaveDays) || 0), 0);

  const all = [...leaveRequests, ...employeeLeaves];
  const used = sumDays(all, ['APPROVED']);
  const reserved = sumDays(all, ['PENDING']);
  const remaining = total - used - reserved;

  return { entitlement: total, used, reserved, remaining };
};
